import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

import app from "../../core/firebase/firebase";
import { requestKarangoApi } from "../../core/api/karangoTransport";

const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_VAPID_PUBLIC_KEY ||
  "BPozcp-at2BekH42nwBqfl-rUm4tTTFX4wlApap7x2C779BnVOym_Pt-gttJiAhYvnGMiVRq0Hzen7GXeLVEfQY";
const MESSAGING_SERVICE_WORKER_URL = "/firebase-messaging-sw.js";
const PUSH_REGISTRATION_STORAGE_KEY = "karango.push.registration";
const SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 12_000;

export const PUSH_ERROR_CODES = Object.freeze({
  UNSUPPORTED: "push/unsupported",
  PERMISSION_DENIED: "push/permission-denied",
  SERVICE_WORKER_FAILED: "push/service-worker-failed",
  TOKEN_FAILED: "push/token-failed",
  SERVER_REGISTRATION_FAILED: "push/server-registration-failed",
  NO_ACTIVE_TOKEN: "push/no-active-token",
  TEST_FAILED: "push/test-failed",
});

let messagingInstance = null;
let serviceWorkerRegistrationPromise = null;
let tokenRegistrationPromise = null;

function makePushError(code, cause) {
  const error = new Error(code);
  error.code = code;
  error.cause = cause;
  return error;
}

function readStoredRegistrationId() {
  try {
    const value = window.localStorage.getItem(PUSH_REGISTRATION_STORAGE_KEY);
    const parsed = value ? JSON.parse(value) : null;
    return typeof parsed?.id === "string" ? parsed.id : null;
  } catch {
    return null;
  }
}

function persistRegistrationId(id) {
  try {
    if (id) {
      window.localStorage.setItem(
        PUSH_REGISTRATION_STORAGE_KEY,
        JSON.stringify({ id })
      );
    } else {
      window.localStorage.removeItem(PUSH_REGISTRATION_STORAGE_KEY);
    }
  } catch {
    // Storage is an optimization only; FCM remains functional without it.
  }
}

async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance;
  if (!(await isSupported())) return null;
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

function waitForServiceWorkerActivation(registration) {
  if (registration.active) return Promise.resolve(registration);

  const worker = registration.installing || registration.waiting;
  if (!worker) {
    return Promise.reject(
      makePushError(PUSH_ERROR_CODES.SERVICE_WORKER_FAILED)
    );
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(makePushError(PUSH_ERROR_CODES.SERVICE_WORKER_FAILED));
    }, SERVICE_WORKER_ACTIVATION_TIMEOUT_MS);

    const handleStateChange = () => {
      if (worker.state === "activated") {
        window.clearTimeout(timeoutId);
        worker.removeEventListener("statechange", handleStateChange);
        resolve(registration);
      } else if (worker.state === "redundant") {
        window.clearTimeout(timeoutId);
        worker.removeEventListener("statechange", handleStateChange);
        reject(makePushError(PUSH_ERROR_CODES.SERVICE_WORKER_FAILED));
      }
    };

    worker.addEventListener("statechange", handleStateChange);
    handleStateChange();
  });
}

export async function getFirebaseMessagingServiceWorkerRegistration() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    throw makePushError(PUSH_ERROR_CODES.UNSUPPORTED);
  }

  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = navigator.serviceWorker
      .register(MESSAGING_SERVICE_WORKER_URL, {
        scope: "/",
        updateViaCache: "none",
      })
      .then(waitForServiceWorkerActivation)
      .catch((error) => {
        serviceWorkerRegistrationPromise = null;
        if (error?.code) throw error;
        throw makePushError(PUSH_ERROR_CODES.SERVICE_WORKER_FAILED, error);
      });
  }

  return serviceWorkerRegistrationPromise;
}

export async function checkPushSupport() {
  if (typeof Notification === "undefined") return false;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }

  try {
    return await isSupported();
  } catch {
    return false;
  }
}

export async function requestPushPermission() {
  if (typeof Notification === "undefined") {
    throw makePushError(PUSH_ERROR_CODES.UNSUPPORTED);
  }

  const permission = await Notification.requestPermission();
  if (permission === "denied") {
    throw makePushError(PUSH_ERROR_CODES.PERMISSION_DENIED);
  }
  return permission;
}

export async function getOrRegisterPushToken() {
  if (tokenRegistrationPromise) return tokenRegistrationPromise;

  tokenRegistrationPromise = (async () => {
    if (
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    ) {
      throw makePushError(PUSH_ERROR_CODES.PERMISSION_DENIED);
    }

    const messaging = await getMessagingInstance();
    if (!messaging) throw makePushError(PUSH_ERROR_CODES.UNSUPPORTED);

    const serviceWorkerRegistration =
      await getFirebaseMessagingServiceWorkerRegistration();

    let token;
    try {
      token = await getToken(messaging, {
        vapidKey: VAPID_PUBLIC_KEY,
        serviceWorkerRegistration,
      });
    } catch (error) {
      throw makePushError(PUSH_ERROR_CODES.TOKEN_FAILED, error);
    }

    if (!token) throw makePushError(PUSH_ERROR_CODES.TOKEN_FAILED);

    let payload;
    try {
      payload = await requestKarangoApi("/v1/push-tokens", {
        method: "POST",
        requiresAuth: true,
        body: {
          token,
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          permission: "GRANTED",
        },
      });
    } catch (error) {
      throw makePushError(PUSH_ERROR_CODES.SERVER_REGISTRATION_FAILED, error);
    }

    const pushToken = payload?.pushToken;
    if (!pushToken?.id) {
      throw makePushError(PUSH_ERROR_CODES.SERVER_REGISTRATION_FAILED);
    }

    persistRegistrationId(pushToken.id);
    return pushToken;
  })().finally(() => {
    tokenRegistrationPromise = null;
  });

  return tokenRegistrationPromise;
}

export async function revokePushToken(tokenId) {
  if (!tokenId) return null;
  return requestKarangoApi(
    `/v1/push-tokens/${encodeURIComponent(String(tokenId))}`,
    { method: "DELETE", requiresAuth: true }
  );
}

export async function unregisterPushDevice() {
  const registrationId = readStoredRegistrationId();

  if (registrationId) {
    try {
      await revokePushToken(registrationId);
    } catch (error) {
      console.warn("[push] token distant non revoque:", error);
    }
  }

  try {
    const messaging = await getMessagingInstance();
    if (messaging) await deleteToken(messaging);
  } catch (error) {
    console.warn("[push] token local non supprime:", error);
  } finally {
    persistRegistrationId(null);
  }

  return { disabled: true };
}

export async function sendTestPushNotification(
  pushTokenId,
  { onRequestStarted } = {}
) {
  try {
    return await requestKarangoApi("/v1/push-tokens/test", {
      method: "POST",
      requiresAuth: true,
      body: pushTokenId ? { pushTokenId } : {},
      onRequestStarted,
    });
  } catch (error) {
    if (error?.code === PUSH_ERROR_CODES.NO_ACTIVE_TOKEN) throw error;
    throw makePushError(PUSH_ERROR_CODES.TEST_FAILED, error);
  }
}

export function resolveSafePushLink(value) {
  if (typeof window === "undefined" || typeof value !== "string") return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (_error) {
    return null;
  }
}

export function onForegroundMessage(callback) {
  let cancelled = false;
  let unsubscribe = () => {};

  getMessagingInstance()
    .then((messaging) => {
      if (cancelled || !messaging) return;
      unsubscribe = onMessage(messaging, callback);
    })
    .catch(() => {});

  return () => {
    cancelled = true;
    unsubscribe();
  };
}
